/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable arrow-body-style */
import {
 Autocomplete,
 CircularProgress,
 SxProps,
 TextField,
 Theme,
 useTheme,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import {
 ReactNode,
 useCallback,
 useEffect,
 useMemo,
 useRef,
 useState,
} from 'react';
import {
 Control,
 Controller,
 Path,
 RegisterOptions,
 UseFormSetValue,
 useWatch,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { get, post } from '../../api/api';
import { displayToast } from '../../redux/toast/toastSlice';
import { FormValues } from '../../types/types';
import { getModelInfo } from '@/utils/structureUtils';

const SelectApi = <T extends FormValues = FormValues>({
 name,
 url,
 filter,
 label,
 isEditable,
 operator,
 control,
 setValue,
 multiple = false,
 disabled = false,
 isGet = false,
 styles,
 onSelect,
 rules,
 onSearchResult,
 id,
 noOptionsText,
 clearOnBlur,
}: {
 name: Path<T>;
 url: string;
 filter: string;
 label: string;
 isEditable?: boolean;
 operator: 'contains' | 'eq';
 control: Control<T, any>;
 setValue: UseFormSetValue<T>;
 multiple?: boolean;
 disabled?: boolean;
 isGet?: boolean;
 styles?: SxProps<Theme> | ((theme: Theme) => SxProps<Theme>);
 onSelect?: (value: any) => void;
 rules?: Omit<
  RegisterOptions<T, Path<T>>,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
 >;
 /** Called after every successful options fetch with the trimmed search
  *  term and the rows returned (empty when nothing matched). */
 onSearchResult?: (term: string, options: unknown[]) => void;
 /** Forwarded to the underlying Autocomplete/input id — lets a caller that
  *  renders its own external <label htmlFor=...> (e.g. weighing V2, which
  *  passes `label=""` to suppress MUI's own floating label) associate it. */
 id?: string;
 /** Rendered inside the popper when the fetch returned no rows — a caller
  *  can put an action there (e.g. weighing V2's "create vehicle"), which is
  *  the only spot reachable while the popper covers what sits below. */
 noOptionsText?: ReactNode;
 /** MUI default (true) wipes unselected typed text on blur, which also
  *  refires the search with '' here. Pass false to keep the term. */
 clearOnBlur?: boolean;
}) => {
 const theme = useTheme();
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const [options, setOptions] = useState([]);
 const [loading, setLoading] = useState(false);
 const [inputValue, setInputValue] = useState('');
 const selectedValue = useWatch({ control, name });
 const filterName = getModelInfo(filter).name;
 // Held in a ref, never in fetchOptions' deps: callers commonly pass an
 // inline arrow, and a new identity per render would rebuild fetchOptions →
 // debouncedFetchOptions → run the cleanup effect below, whose .clear()
 // silently drops the pending debounced search the user just typed.
 const onSearchResultRef = useRef(onSearchResult);
 onSearchResultRef.current = onSearchResult;

 const getOptionName = (option: any) => {
  if (option === null || option === undefined) return '';
  const id = option?.Id ?? option;
  return (
   options.find((o) => (o as any)?.Id === id)?.[filterName] ||
   option?.[filterName] ||
   option?.Name ||
   // Sparse display fields (e.g. Results.ExternalCode) would otherwise render
   // blank, unselectable-looking options — fall back to the record id.
   (id !== null && id !== undefined && typeof id !== 'object' ? `#${id}` : '')
  ).toString();
 };

 //  console.log(name, filter, url);

 const fetchOptions = useCallback(
  async (val?: string) => {
   // Structure fields without a resolvable lookup url (misconfigured backend
   // structure) would otherwise throw on url.replace and log on every open.
   if (!url) {
    setOptions([]);
    return;
   }
   setLoading(true);
   try {
    const apiParams = JSON.parse(filter);

    // Numeric input also matches the record id — targets with sparse display
    // fields (e.g. Results, labeled "#Id") are otherwise unsearchable. Order
    // matters: default filters first, then the display search ("Or" before the
    // id fallback), id last — the flat left-to-right chain then reads
    // `defaults AND display OR id`.
    const trimmedVal = val?.trim() ?? '';
    const isNumericSearch = /^\d+$/.test(trimmedVal);
    const searchFilters = val
     ? [
        {
         field: filterName,
         operator,
         value: val,
         ...(isNumericSearch ? { logic: 'Or' } : {}),
        },
        ...(isNumericSearch
         ? [{ field: 'Id', operator: 'eq', value: Number(trimmedVal) }]
         : []),
       ]
     : [];
    const filtersData = val
     ? [...(apiParams?.filters || []), ...searchFilters]
     : apiParams?.filters;
    if (Array.isArray(filtersData) && filtersData.length > 1) {
     filtersData.forEach((item, index) => {
      if (index < filtersData.length - 1 && !item.logic) {
       item.logic = 'And';
      }
     });
    }
    const response = await (isGet ? get : post)(url.replace('/api', ''), {
     ...apiParams,
     filters: filtersData,
    });
    setOptions(response.data || []);
    onSearchResultRef.current?.((val ?? '').trim(), response.data || []);
   } catch (error) {
    console.error('Failed to load options:', error);
    dispatch(
     displayToast({ message: t('global.operationFailed'), type: 'error' }),
    );
    setOptions([]);
   } finally {
    setLoading(false);
   }
  },
  [dispatch, filter, filterName, isGet, operator, t, url],
 );

 const debouncedFetchOptions = useMemo(
  () => debounce((val: string) => fetchOptions(val), 500),
  [fetchOptions],
 );

 useEffect(() => {
  if (multiple) {
   return;
  }

  // No selection: leave the input alone — this effect re-runs when fetched
  // options land, and clearing here would wipe the text mid-typing (the
  // clear-on-deselect case is handled by the effect below).
  if (!selectedValue) {
   return;
  }

  const nextValue = getOptionName(selectedValue);
  if (!nextValue) {
   return;
  }

  setInputValue((prev) => (prev ? prev : nextValue));
 }, [multiple, selectedValue, options]);

 // Clear the input only when the selection itself is cleared (form reset,
 // external deselect) — never on option-list updates.
 useEffect(() => {
  if (!multiple && !selectedValue) {
   setInputValue('');
  }
 }, [multiple, selectedValue]);

 useEffect(() => {
  return () => {
   debouncedFetchOptions.clear();
  };
 }, [debouncedFetchOptions]);

 return (
  control && (
   <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState }) => {
     //  console.log('selectApi field', field);
     return (
      <Autocomplete
       {...field}
       id={id}
       multiple={multiple}
       {...(noOptionsText !== undefined && { noOptionsText })}
       {...(clearOnBlur !== undefined && { clearOnBlur })}
       onOpen={() => {
        // Re-opening a lookup that already HAS a selection: inputValue is then
        // the selected option's own label, and feeding it back as a `contains`
        // filter re-runs exactly the search the 'selectOption' branch below
        // was fixed to stop firing — the same request that answers 400 where
        // the backend does not accept the display field as a filter. Re-open
        // should show the unfiltered list anyway, so drop the term.
        const showsSelectionLabel =
         !!field.value && inputValue === getOptionName(field.value);
        fetchOptions(showsSelectionLabel ? '' : inputValue);
       }}
       disabled={disabled || !isEditable}
       value={field.value || (multiple ? [] : null)}
       inputValue={inputValue}
       onChange={(_, value: any) => {
        // console.log('onChange', value, _);
        if (!multiple) {
         setInputValue(value ? getOptionName(value) : '');
        }
        setValue(
         name,
         value || (multiple ? [] : null),
         {
          shouldDirty: true,
          shouldTouch: true,
         }
        );
        onSelect?.(value || (multiple ? [] : null));
       }}
       options={options}
       renderOption={(props, option) => (
        <li {...props} key={`${filterName}-${option[filterName]}-${option.Id}`}>
         {getOptionName(option)}
        </li>
       )}
       getOptionLabel={(option: any) => getOptionName(option)}
       filterOptions={(x) => x} // Disable client-side filtering
       onInputChange={(_, val, reason) => {
        // console.log('onInputChange', reason, field.value, val);
        // 'selectOption' is grouped with 'reset' because neither is the user
        // searching. MUI's selectNewValue calls resetInputValue with the
        // 'selectOption' reason when an option is picked
        // (@mui/material/useAutocomplete/useAutocomplete.js:521 → :139-152),
        // and it does so BEFORE onChange runs — so the input text is already
        // about to be set by the onChange handler above, and returning here
        // leaves it consistent with how 'reset' is handled. Falling through
        // instead debounce-fired a second, pointless options request filtered
        // on the label just selected; where the backend does not accept that
        // field as a filter it answered 400 and the catch below raised an
        // error toast (seen live on the cards Driver lookup).
        if (reason === 'reset' || reason === 'selectOption') {
         return;
        }
        if (reason === 'clear') {
         setInputValue('');
         //  setValue('');
         //  clearValue();
         return;
        }

        if (field.value === val) return;
        setInputValue(val);
        debouncedFetchOptions(val);

        // if (field.value[filterName] === value) return;
        // setInputValue(value);
        // fetchOptions(value);
       }}
       isOptionEqualToValue={(option, value) => {
        return (option as any).Id === value?.Id;
       }}
       loading={loading}
       renderInput={(params) => (
        <TextField
         {...params}
         label={label}
         variant="outlined"
         disabled={disabled || !isEditable}
         error={!!fieldState.error}
         helperText={fieldState.error?.message}
         sx={
          typeof styles === 'function'
           ? styles(theme)
           : (styles as SxProps<Theme>) ||
             {
              //   backgroundColor: 'transparent',
             }
         }
         //  slotProps={{
         //   input: {
         //    ...params.inputProps,
         //    sx: {
         //     // backgroundColor: 'white',
         //    },
         //   },
         //  }}
         InputProps={{
          ...params.InputProps,
          endAdornment: (
           <>
            {loading ? (
             <CircularProgress
              color="inherit"
              size={20}
              sx={{ pointerEvents: 'none' }}
             />
            ) : null}
            {params.InputProps.endAdornment}
           </>
          ),
         }}
        />
       )}
       sx={{ width: '100%' }}
      />
     );
    }}
   />
  )
 );
};
export default SelectApi;
